import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useRequestData, usePrefs, useLabels } from 'wsm-common-data';
import logger from 'ws-scripts/modules/logger';
import { ErrorBoundary } from 'wsm-error-boundary';
import {
	addNewRelicPageAction,
	setNewRelicCustomAttribute
} from '@ddc/new-relic';
import { isBrowser } from 'ws-scripts/modules/environment';
import { insertWIAPIImagesByInventory } from '../utilities/insertWIAPIImages';
import VehicleCards from '../components/VehicleCards';
import SeoVehicleCards from '../components/seo/SeoVehicleCards';
import SeoPagination from '../components/seo/SeoPagination';
import CompareBar from '../components/CompareBar';
import ErrorAlert from '../components/ErrorAlert';
import MediaModalContainer from '../components/MediaModalContainer';
import LowResults from '../components/noResults/LowResults';
import NewCarCustomizer from '../components/noResults/NewCarCustomizer';
import NoResults from '../components/NoResults';
import InfoAlert from '../components/InfoAlert';
import { waitForInventory } from '../utilities/waitForInvData';
import './style.scss';
import { getIsSmallScreen } from '../utilities/layout';
import { setLayout } from '../features/layout';
import { setUserProfile } from '../features/userProfile';
import { setAccountData } from '../features/accountData';
import { hydrateCompareFromStorage } from '../features/compare';

const Widget = () => {
	const { deviceType, widgetName, windowId } = useRequestData();
	const {
		imageDelayLoad,
		lowResultsCount,
		showNccLinkNoLowResults,
		showVideoModalButton,
		showSRPWindowSticker
	} = usePrefs();
	const labels = useLabels();
	const { pageAlias } = useSelector((state) => state.widgetInfo);
	const layout = useSelector((state) => state.layout);
	const seoVehicleData = useSelector((state) => state.seoVehicleData);
	const dispatch = useDispatch();

	const delayTimer = useRef(0);
	const subsequentFailureRef = useRef(null);

	const initialInv = [...Array(6)].map((_, i) => ({
		uuid: `${i}`,
		isPlaceholder: true
	}));

	const [inventory, setInventory] = useState({
		accounts: {},
		incentives: {},
		pageInfo: {
			enableMediaCarousel: false
		},
		inventory: initialInv
	});
	const [appliedFilters, setAppliedFilters] = useState([]);
	const [alertMessage, setAlertMessage] = useState({});
	// if the imageDelayLoad pref is set, set the loading delay to true
	const [loadingDelayed, setLoadingDelayed] = useState(+imageDelayLoad > 0);
	const [isTransition, setIsTransition] = useState(false);
	const [requestFailed, setRequestFailed] = useState(false);
	// A filter/sort/page/search update failed while results were
	// already on screen. Previous results stay visible; a notice offers retry.
	// Kept apart from requestFailed, which replaces the whole listing.
	const [subsequentFailure, setSubsequentFailure] = useState(false);

	// isLoading is true on initial page load
	// isRequesting is true when facet/filter interaction triggered
	const [isLoading, setIsLoading] = useState(true);
	const [isRequesting, setIsRequesting] = useState(false);
	const [isInitialRequest, setIsInitialRequest] = useState(true);
	// State variables stored in refs for accessing the current value inside callback functions
	const isLoadingRef = useRef();
	const isInitialRequestRef = useRef();
	isLoadingRef.current = isLoading;
	isInitialRequestRef.current = isInitialRequest;

	function removeWidgetHeight() {
		const widgetAppId = `${windowId}-app-root`;
		const widget = document.getElementById(widgetAppId);

		if (widget) {
			widget.classList.remove('min-vh-100');
		}
	}

	// handler for inserting new images on wiapi event
	function handleImagesInsertEventlistener(data) {
		if (isBrowser && window.DDC?.InvData?.srpReady) {
			// get the inventory
			const { inventory: globalInventory } =
				window.DDC.InvData.getInventory();
			if (globalInventory) {
				// build the updated inventory with inserted 3rd party content
				const updatedInventory = insertWIAPIImagesByInventory(
					globalInventory,
					data.detail.content
				);
				// if imageDelayLoad preference is set, clear the delayTimer
				if (+imageDelayLoad > 0) {
					clearTimeout(delayTimer.current);
				}

				// update state with the new inventory and toggle the loading delay off
				setInventory((prevInventory) => ({
					...prevInventory,
					inventory: updatedInventory
				}));
				setLoadingDelayed(false);
			}
		}
	}

	function initializeListings() {
		const invData = window.DDC.InvData;
		// get the global inventory meta data
		const globalData = invData?.inventory || {};

		// Best effort: a throw in 3rd party content must not stop us reaching
		// setIsLoading(false) below, which gates the wsInvListingRendered flag.
		let updatedInventory;
		try {
			// get the new gallery content to be inserted
			const galleryContent = window?.DDC?.PrivateAPI?.getAllGalleryContent
				? window.DDC.PrivateAPI.getAllGalleryContent('vehicle-media')
				: {};

			// accountData is needed when we are attempting to render window sticker ctas
			if (showSRPWindowSticker !== 'none') {
				dispatch(setAccountData(globalData.accounts));
			}

			// build the updated inventory with inserted 3rd party content
			if (invData?.srpReady) {
				updatedInventory = insertWIAPIImagesByInventory(
					globalData.inventory,
					galleryContent
				);
			}
		} catch (error) {
			logger.error(`Error inserting 3rd party content: ${error.message}`);
		}
		// get alert message
		const alertMessageFromData = Object.prototype.hasOwnProperty.call(
			invData || {},
			'alertMessage'
		)
			? invData?.alertMessage
			: {};

		// build initialized state
		setIsRequesting(false);
		setIsLoading(false);
		setRequestFailed(false);
		setSubsequentFailure(false);
		setInventory({
			...globalData,
			...(updatedInventory && { inventory: updatedInventory })
		});
		setAlertMessage(alertMessageFromData);

		removeWidgetHeight();
	}

	// AC4: Restore compare selections from sessionStorage on mount
	useEffect(() => {
		dispatch(hydrateCompareFromStorage());
	}, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only hydration; sessionStorage only matters at first read

	// TA4088241: measure the AI-bot SSR misclassification rate. `isLoading` flips
	// false once the client's live ws-inv-data request resolves, so this fires when
	// a JS-running user agent stuck around for the WIS response on a bot-classified
	// page — a misidentified human OR a JS-capable bot, counted together (teasing the
	// two apart isn't worth the interaction listeners it would take). A headless
	// non-JS crawler never runs it. Why + how to query:
	// docs/feat/US1994526-seo-vehicle-cards/TA4088241-misclassification-telemetry.md
	useEffect(() => {
		if (isBrowser && !isLoading && seoVehicleData.inventory.length > 0) {
			setNewRelicCustomAttribute('aiBotClientSideHydrated', true);
		}
	}, [isLoading, seoVehicleData]);

	useEffect(() => {
		if (!isBrowser) {
			return undefined;
		}
		const stopWaitingForInventory = waitForInventory(widgetName);
		const { innerWidth: viewportWidth } = window;
		dispatch(setLayout({ isSmallScreen: getIsSmallScreen(viewportWidth) }));

		return stopWaitingForInventory;
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		// If imageDelayLoad preference is set, start the delay timer
		// This causes a rerender
		if (+imageDelayLoad > 0) {
			delayTimer.current = setTimeout(() => {
				// toggle delay loading off when the timer expires
				setLoadingDelayed(false);
			}, +imageDelayLoad);
		}

		return () => {
			clearTimeout(delayTimer.current);
		};
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		// This causes a rerender
		// Listen for event to update with new images
		if (isBrowser) {
			window.addEventListener(
				'wiapiAllVehiclesMediaUpdated',
				handleImagesInsertEventlistener
			);
		}

		// Send info to NR about default layout
		setNewRelicCustomAttribute('srpLayout', layout.layout);

		if (isBrowser && window.DDC?.dataLayer?.page?.attributes) {
			window.DDC.dataLayer.page.attributes.srpLayout = layout.layout;
		}

		if (isBrowser && window.DDC?.InvData?.inventory?.inventory) {
			initializeListings();
		}

		return () => {
			window.removeEventListener(
				'wiapiAllVehiclesMediaUpdated',
				handleImagesInsertEventlistener
			);
		};
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		const pubsub = (isBrowser && window.DDC?.pubsub) || undefined;

		if (pubsub) {
			// Subscribing to fetch request events in ws-inv-data
			pubsub.subscribe('ws-inv-data/inventory-request', () => {
				setSubsequentFailure(false);
				if (!isLoadingRef.current && !isInitialRequestRef.current) {
					setIsRequesting(true);
				}
				if (isInitialRequestRef.current) {
					setIsInitialRequest(false);
				}
			});

			// These (both of these initializeListings calls in total) cause 1 rerender
			window.DDC.pubsub.subscribe('ws-inv-data/inventory', () => {
				initializeListings();
			});
			// There is a chance that the topic 'ws-inv-data/inventory' is published by inv-data
			// before the inv-listing subscribes to it. This results in the listings being stuck
			// in the skeleton loader phase. This issue does not happen when faceting.

			// We fire the initializeListings function under these circumstances:
			// When the component mounts (ie: does not trigger on facet interactions),
			// AND when the state variable 'isLoading' is still true
			// AND when the DDC. InvData.inventory object is not empty (data has already been fetched)
			if (
				isBrowser &&
				isLoading &&
				window.DDC?.InvData?.inventory?.inventory
			) {
				initializeListings();
			}

			// This causes a rerender
			pubsub.subscribe('ws-inv-data/facets', () => {
				setAppliedFilters(window.DDC.InvData.getAppliedFilters());
			});
			pubsub.subscribe('ws-inv-data/request-failed', (payload) => {
				if (payload?.isSubsequentCall === true) {
					setIsRequesting(false);
					setSubsequentFailure(true);
					return;
				}
				setRequestFailed(true);
				setIsLoading(false);
				removeWidgetHeight();
			});
			pubsub.subscribe('instant-ePrice-completed', () => {
				// RTK (immer) auto freezes the state tree,
				// so we are creating a deep clone to avoid freezing the global `window.DDC.userProfile` object.
				// (This object gets updated in `cms-web/htdocs/v9/media/js/ddc/v1/userProfileController.js`.)
				let profile = structuredClone(window.DDC?.userProfile);

				// Validate if the profile is a non-empty object
				const isValidProfile =
					typeof profile === 'object' &&
					Object.keys(profile).length > 0;

				profile = isValidProfile ? profile : {};
				dispatch(setUserProfile(profile));
			});
			// Transition display for mobile on ws-inv-filters events
			// This causes rerender
			if (deviceType === 'MOBILE') {
				pubsub.subscribe('ws-inv-filters/modal-show', () => {
					setIsTransition(true);
				});
				pubsub.subscribe('ws-inv-filters/modal-hide', () => {
					setIsTransition(false);
				});
			}
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// Scroll the SRP interaction failure into view
	useEffect(() => {
		if (subsequentFailure) {
			subsequentFailureRef.current?.scrollIntoView?.({
				behavior: 'smooth',
				block: 'nearest'
			});
		}
	}, [subsequentFailure]);

	useEffect(() => {
		let loadListener;
		if (isBrowser && !isLoading && !requestFailed) {
			// scroll to anchor link if it exists
			const { hash } = window.location;
			if (hash) {
				if (document.readyState === 'complete') {
					window.DDC.scrollToAnchor($(hash));
				} else {
					loadListener = () => {
						window.DDC.scrollToAnchor($(hash));
					};
					window.addEventListener('load', loadListener);
				}
			}
		}

		return () => {
			window.removeEventListener('load', loadListener);
		};
	}, [isLoading, requestFailed]);

	useEffect(() => {
		if (isBrowser && !isLoading) {
			const pubsub = window.DDC?.pubsub || undefined;

			// Set a flag so the Web Integration API knows the initial inventory view has rendered.
			// Set before the publish below: the bus does not isolate subscriber throws.
			window.DDC.PrivateAPI = window.DDC.PrivateAPI || {};
			window.DDC.PrivateAPI.wsInvListingRendered = true;

			if (pubsub) {
				try {
					pubsub.publish('ws-inv-listing-update');
				} catch (error) {
					logger.error(
						`Error publishing ws-inv-listing-update: ${error.message}`
					);
				}
			}

			// Required refresh to allow `.dialog` elements to initialize jQuery
			// dialog and slide-in functionality on wsm-vehicle-cta-display buttons
			if (deviceType === 'MOBILE') {
				window.DDC.mobile.slidein.init();
			} else if (typeof $ === 'function') {
				$(document).trigger('modulesRefresh', {
					modules: {
						dialog: window.DDC.modules.dialog
					}
				});
			}
		}
	}, [isLoading, inventory, deviceType]);

	const { pageInfo = {} } = inventory;
	const { enableMediaCarousel } = pageInfo;
	const isNoResults = inventory?.pageInfo?.totalCount === 0;
	const isAlertMessageEmpty =
		alertMessage === null ||
		alertMessage === undefined ||
		(Object.keys(alertMessage).length === 0 &&
			alertMessage.constructor === Object);
	const isMyCarsPage = pageAlias === 'INVENTORY_LISTING_SHOPPING_ACTIVITY';
	// "Low result count" applies to the regular SRP only; the MyCars page is branched separately below.
	const isLowResult =
		inventory?.pageInfo?.totalCount <= +lowResultsCount &&
		+lowResultsCount > 0;

	// Phase A (AI-bot SSR path): ws-inv-listing's own store is seeded with SEO
	// inventory server-side (TA4063705). Render static crawlable cards while
	// isLoading is still true; the live window.DDC.InvData path (Phase B) flips
	// isLoading false after mount and swaps in the interactive VehicleCards.
	// Driven only by serialized Redux state + the initial isLoading, so the
	// server render and the client's first render match (clean hydration).
	const isSeoPhase = isLoading && seoVehicleData.inventory.length > 0;

	if (requestFailed) {
		addNewRelicPageAction('WS INV LISTING REQUEST FAILED', {
			requestFailed: true
		});
	}

	// ws-inv-data may predate retryLastRequest(); without it, show message only.
	const canRetry =
		isBrowser &&
		typeof window.DDC?.InvData?.retryLastRequest === 'function';
	const handleRetry = () => {
		setSubsequentFailure(false);
		window.DDC.InvData.retryLastRequest();
	};

	const vehicleCards = (
		<VehicleCards
			{...{
				inventoryObj: inventory,
				enableMediaCarousel,
				isLoading,
				isTransition,
				isRequesting,
				loadingDelayed,
				appliedFilters
			}}
		/>
	);
	const lowResultsSection = (
		<>
			<LowResults
				isNoResults={isNoResults}
				appliedFilters={appliedFilters}
			/>
			{showNccLinkNoLowResults === 'true' && <NewCarCustomizer />}
		</>
	);

	let listingContent;
	if (isSeoPhase) {
		listingContent = (
			<>
				<SeoVehicleCards inventory={seoVehicleData.inventory} />
				<SeoPagination
					pageInfo={seoVehicleData.pageInfo}
					baseUrl={seoVehicleData.baseUrl}
					parameterMap={seoVehicleData.parameterMap}
				/>
			</>
		);
	} else if (isNoResults) {
		if (isMyCarsPage) {
			listingContent = !isAlertMessageEmpty ? (
				<InfoAlert {...{ alertMessage }} />
			) : (
				<NoResults {...{ appliedFilters }} />
			);
		} else {
			listingContent = lowResultsSection;
		}
	} else if (isMyCarsPage) {
		listingContent = (
			<>
				{vehicleCards}
				{!isAlertMessageEmpty && <InfoAlert {...{ alertMessage }} />}
			</>
		);
	} else {
		listingContent = (
			<>
				{vehicleCards}
				{isLowResult && lowResultsSection}
			</>
		);
	}

	return (
		<ErrorBoundary
			errorHandler={(error, errorInfo) => {
				setNewRelicCustomAttribute(
					'SRP ERROR',
					`ws-inv-listing error boundary.\n${error}`
				);
				const newError = new Error(
					`ws-inv-listing error boundary.\n${error}`
				);
				newError.originalError = error;
				newError.originalStackTrace = errorInfo.componentStack;
				logger.error(`${newError}\n${newError.originalStackTrace}`);
			}}
		>
			{requestFailed ? (
				<ErrorAlert
					errorLabel={labels.get('TECHNICAL_DIFFICULTIES_VERBIAGE')}
				/>
			) : (
				<>
					<div
						role="region"
						aria-label={labels.get('MATCHING_VEHICLES')}
						// TODO add data-async-widget="true" to widget container renderer in ws-scripts
						data-async-widget
					>
						{subsequentFailure && (
							<div role="alert" ref={subsequentFailureRef}>
								<ErrorAlert
									errorLabel={labels.get(
										'TECHNICAL_DIFFICULTIES_VERBIAGE'
									)}
									retryLabel={labels.get('TRY_AGAIN')}
									onRetry={canRetry ? handleRetry : undefined}
								/>
							</div>
						)}
						{listingContent}
						{showVideoModalButton === 'true' && (
							<MediaModalContainer />
						)}
					</div>
					<CompareBar />
				</>
			)}
		</ErrorBoundary>
	);
};

export default Widget;
